import * as React from 'react';

import { Button, Label, Tab, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePowerOff, LucideTrash2 } from 'lucide-react';

import { ConfirmationDialog } from '../_shared/ConfirmationDialog';
import { TabWithWarning } from '../_shared/TabWithWarning';

import { useCoreUserDisableMutation } from './_graphql/useCoreUserDisableMutation';
import { useCoreUsersDeleteMutation } from './_graphql/useCoreUsersDeleteMutation';
import { useUserQuery } from './_graphql/useUserQuery';
import { useInitUserRoleFormFromApi } from './hooks/useInitUserRoleFormFromApi';
import { UserContactInfoTab } from './UserContactInfoTab';
import { UserFacilitiesTab } from './UserFacilitiesTab';
import { UserPreferencesTab } from './UserPreferencesTab';
import { UserRolesTab } from './UserRolesTab';

import { SettingsSubmodule } from '~/modules/settings/_components/_layouts/SettingsSubmodule';
import { formatDate } from '~/modules/settings/_components/_shared/utils';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import {
  $currentUser,
  $initUserForm,
  $userId,
  $userDetailsTab,
  $userRoleForm,
  type UserDetailsTab,
} from '~/modules/settings/_state/userForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { PermissionGate } from '~/shared/components/PermissionGate';

export const UserDetailsView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const userId = useAtomValue($userId);
  const [currentTab, setCurrentTab] = useAtom($userDetailsTab);
  const currentUser = useAtomValue($currentUser);
  const setCurrentUser = useSetAtom($currentUser);
  const initForm = useSetAtom($initUserForm);

  const { data: userData } = useUserQuery({ userId });
  const { toast } = useToast();
  const disableMutation = useCoreUserDisableMutation();
  const deleteMutation = useCoreUsersDeleteMutation();

  const [removeAccessDialogOpen, setRemoveAccessDialogOpen] =
    React.useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] =
    React.useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = React.useState('');

  const { hasRoleFormLoaded } = useInitUserRoleFormFromApi(userId);

  React.useEffect(() => {
    if (userData?.user) {
      setCurrentUser(userData.user);
    }
  }, [userData, setCurrentUser]);

  React.useEffect(() => {
    if (currentUser) {
      initForm();
    }
  }, [currentUser, initForm]);

  const handleBack = () => {
    setLocation('/team-and-permissions/users');
  };

  const userFullName =
    currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : '';

  const handleRemoveAccessClick = () => setRemoveAccessDialogOpen(true);

  const handleRemoveAccessConfirm = async () => {
    if (!userId) return;
    try {
      await disableMutation.mutateAsync({ id: userId });
      setRemoveAccessDialogOpen(false);
      toast({ title: 'Access removed', variant: 'success' });
    } catch (err) {
      toast({
        title: 'Failed to remove access',
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    }
  };

  const handleDeleteAccountClick = () => {
    setDeleteConfirmName('');
    setDeleteAccountDialogOpen(true);
  };

  const handleDeleteAccountConfirm = async () => {
    if (!userId || deleteConfirmName.trim() !== userFullName) return;
    try {
      await deleteMutation.mutateAsync({ userIds: [userId] });
      setDeleteAccountDialogOpen(false);
      toast({ title: 'User deleted', variant: 'success' });
      setLocation('/team-and-permissions/users');
    } catch (err) {
      toast({
        title: 'Failed to delete user',
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    }
  };

  const handleRemoveAccessDialogOpenChange = (open: boolean) => {
    if (!disableMutation.isPending) setRemoveAccessDialogOpen(open);
  };

  const handleDeleteAccountDialogOpenChange = (open: boolean) => {
    if (!open && !deleteMutation.isPending) {
      setDeleteConfirmName('');
      setDeleteAccountDialogOpen(false);
    } else if (open) {
      setDeleteAccountDialogOpen(open);
    }
  };

  const isRemoveAccessPending = disableMutation.isPending;
  const isDeletePending = deleteMutation.isPending;
  const canDelete =
    userFullName.length > 0 && deleteConfirmName.trim() === userFullName;

  const handleTabChange = (tab: UserDetailsTab) => {
    setCurrentTab(tab);
  };

  const roleForm = useAtomValue($userRoleForm);

  const hasNoRoleFacilityAssociations =
    hasRoleFormLoaded &&
    !roleForm.assignments.some((a) => a.assignedFacilityIds.length > 0);

  const subtitle = currentUser
    ? `Active since ${formatDate(currentUser.createdAt)}`
    : 'Loading...';

  return (
    <SettingsSubmodule
      className="@UserDetailsView h-full"
      contentClassName="p-0"
      title={
        currentUser
          ? `${currentUser.firstName} ${currentUser.lastName}`
          : 'Loading...'
      }
      subtitle={subtitle}
      onBackPressed={handleBack}
      tools={
        <>
          <PermissionGate action="userUpdate">
            <Button
              variant="outline"
              icon={<LucidePowerOff className="size-4" />}
              onPress={handleRemoveAccessClick}
              isDisabled={
                !currentUser || isRemoveAccessPending || isDeletePending
              }
            >
              {t.teamAndPermissions.users.actions.removeAccess}
            </Button>
          </PermissionGate>
          <PermissionGate action="userDelete">
            <Button
              variant="outline"
              color="failure"
              icon={<LucideTrash2 className="size-4" />}
              onPress={handleDeleteAccountClick}
              isDisabled={
                !currentUser || isRemoveAccessPending || isDeletePending
              }
            >
              {t.teamAndPermissions.users.actions.deleteUser}
            </Button>
          </PermissionGate>
        </>
      }
      tabs={
        <>
          <Tab
            selected={currentTab === 'contact-info'}
            onClick={() => handleTabChange('contact-info')}
          >
            Contact Info & Access
          </Tab>
          <Tab
            selected={currentTab === 'preferences'}
            onClick={() => handleTabChange('preferences')}
          >
            Preferences
          </Tab>
          <TabWithWarning
            label="Roles"
            selected={currentTab === 'roles'}
            onClick={() => handleTabChange('roles')}
            showWarning={hasNoRoleFacilityAssociations}
          />
          <TabWithWarning
            label="Facilities"
            selected={currentTab === 'facilities'}
            onClick={() => handleTabChange('facilities')}
            showWarning={hasNoRoleFacilityAssociations}
          />
        </>
      }
    >
      {currentTab === 'contact-info' && <UserContactInfoTab />}
      {currentTab === 'preferences' && <UserPreferencesTab />}
      {currentTab === 'roles' && <UserRolesTab />}
      {currentTab === 'facilities' && <UserFacilitiesTab />}

      <ConfirmationDialog
        isOpen={removeAccessDialogOpen}
        onOpenChange={handleRemoveAccessDialogOpenChange}
        title={t.teamAndPermissions.users.dialogs.removeAccess.title(
          userFullName,
        )}
        description={t.teamAndPermissions.users.dialogs.removeAccess.description(
          userFullName,
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.removeAccess.remove}
        cancelLabel={t.teamAndPermissions.users.dialogs.removeAccess.discard}
        onConfirm={handleRemoveAccessConfirm}
        onCancel={() => handleRemoveAccessDialogOpenChange(false)}
        isLoading={isRemoveAccessPending}
        className="@UserDetailsViewRemoveAccessDialog"
      />

      <ConfirmationDialog
        isOpen={deleteAccountDialogOpen}
        onOpenChange={handleDeleteAccountDialogOpenChange}
        title={t.teamAndPermissions.users.dialogs.deleteAccount.title(
          userFullName,
        )}
        description={t.teamAndPermissions.users.dialogs.deleteAccount.description(
          userFullName,
        )}
        confirmLabel={t.teamAndPermissions.users.dialogs.deleteAccount.delete}
        cancelLabel={t.teamAndPermissions.users.dialogs.deleteAccount.discard}
        onConfirm={handleDeleteAccountConfirm}
        onCancel={() => handleDeleteAccountDialogOpenChange(false)}
        isLoading={isDeletePending}
        confirmDisabled={!canDelete}
        className="@UserDetailsViewDeleteAccountDialog"
      >
        <div className="space-y-2">
          <Label>
            {
              t.teamAndPermissions.users.dialogs.deleteAccount
                .teamMemberNameLabel
            }
          </Label>
          <TextField
            value={deleteConfirmName}
            onChange={setDeleteConfirmName}
            placeholder={userFullName}
            isDisabled={isDeletePending}
            autoComplete="off"
          />
        </div>
      </ConfirmationDialog>
    </SettingsSubmodule>
  );
};
